/**
 * Focus Flow — Milestone 37.1 · E2E demo route.
 *
 * This page exists only to prove the Focus Flow primitives against a
 * real mission. It is gated by the backend's E2E-only ``/e2e/focus-flow``
 * router — if E2E mode is off, the ``/enabled`` probe 404s and the page
 * redirects to ``/home``.
 *
 * Never link to this route from production navigation. It ships with
 * the framework so Playwright can drive the same primitives every real
 * mission will drive in 37.2+.
 */
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import {
  isFocusFlowDemoEnabled,
  useAdvanceMission,
  useCompleteMission,
  usePauseMission,
  useResumableMissions,
  useResumeMission,
  useStartMission,
  type Mission,
  type MissionStage,
} from "@/entities/missions";
import {
  MissionCompleteHandoff,
  MissionResumeTile,
  MissionShell,
} from "@/features/focus-flow";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useAuthStore } from "@/entities/auth";

const DEMO_TYPE = "focus_flow_demo";
const STAGE_ORDER: MissionStage[] = [
  "started",
  "context",
  "execution",
  "decision",
  "confirmation",
];
const STAGE_LABEL: Record<MissionStage, string> = {
  started:      "Getting Ready",
  context:      "Context",
  execution:    "Reviewing",
  decision:     "Decision",
  confirmation: "Confirm",
};

function nextStage(current: MissionStage): MissionStage | null {
  const idx = STAGE_ORDER.indexOf(current);
  if (idx < 0 || idx >= STAGE_ORDER.length - 1) return null;
  return STAGE_ORDER[idx + 1];
}

export default function FocusFlowDemoPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;
  const userId = useAuthStore((s) => s.user?.id ?? null);

  const [gateChecked, setGateChecked] = useState<"pending" | "ok" | "disabled">("pending");
  const [activeMission, setActiveMission] = useState<Mission | null>(null);
  const [completion, setCompletion] = useState<{
    mission: Mission;
    recommended_next: string | null;
  } | null>(null);

  const resumable = useResumableMissions(workspaceId);
  const startMutation = useStartMission(workspaceId, userId);
  const advanceMutation = useAdvanceMission(workspaceId, userId);
  const pauseMutation = usePauseMission(workspaceId, userId);
  const resumeMutation = useResumeMission(workspaceId, userId);
  const completeMutation = useCompleteMission(workspaceId, userId);
  const navigate = useNavigate();

  // Gate the page on the E2E-only /enabled probe. In production this
  // 404s and the page immediately redirects home.
  useEffect(() => {
    let cancelled = false;
    isFocusFlowDemoEnabled().then((ok) => {
      if (cancelled) return;
      setGateChecked(ok ? "ok" : "disabled");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (gateChecked === "pending") {
    return (
      <main
        className="min-h-screen bg-[color:var(--brand-obsidian,#0B0D12)] flex items-center justify-center"
        data-testid="focus-flow-demo-loading"
      >
        <div className="text-white/60 text-sm">Checking availability…</div>
      </main>
    );
  }
  if (gateChecked === "disabled") {
    return <Navigate to="/home" replace />;
  }

  const demoResumables = (resumable.data?.items ?? []).filter(
    (m) => m.mission_type === DEMO_TYPE || m.mission_type === "focus_flow_demo_followup",
  );

  async function handleStart() {
    if (!workspaceId) return;
    const m = await startMutation.mutateAsync({
      missionType: DEMO_TYPE,
      subject_id: `demo-${Date.now()}`,
      initial_memory: { asset_index: 0, reviewed_ids: [] as string[] },
      priority: 10,
    });
    // Immediately advance from ``started`` → ``context`` so the founder
    // is never sitting on an empty stage. Total = 3 review steps.
    const first = await advanceMutation.mutateAsync({
      missionId: m.id,
      expected_version: m.version,
      next_stage: "context",
      progress: { current: 0, total: 3, step_key: "review" },
    });
    setActiveMission(first);
  }

  async function handleAdvance() {
    if (!activeMission) return;
    const next = nextStage(activeMission.stage);
    if (!next) return;
    const total = 3;
    const current = Math.min(total, activeMission.progress.current + 1);
    const m = await advanceMutation.mutateAsync({
      missionId: activeMission.id,
      expected_version: activeMission.version,
      next_stage: next,
      progress: { current, total, step_key: next },
      memory_patch: {
        asset_index: current,
        reviewed_ids: [
          ...((activeMission.memory.data.reviewed_ids as string[] | undefined) ?? []),
          `asset-${current}`,
        ],
      },
    });
    setActiveMission(m);
  }

  async function handlePause() {
    if (!activeMission) {
      setActiveMission(null);
      return;
    }
    await pauseMutation.mutateAsync({
      missionId: activeMission.id,
      expected_version: activeMission.version,
    });
    setActiveMission(null);
    void resumable.refetch();
  }

  async function handleResume(mission: Mission) {
    const m = mission.status === "paused"
      ? await resumeMutation.mutateAsync({
          missionId: mission.id, expected_version: mission.version,
        })
      : mission;
    setActiveMission(m);
  }

  async function handleComplete() {
    if (!activeMission) return;
    const res = await completeMutation.mutateAsync({
      missionId: activeMission.id,
      expected_version: activeMission.version,
      final_memory_patch: { note: "completed by demo" },
    });
    setActiveMission(null);
    setCompletion({
      mission: res.mission,
      recommended_next: res.handoff.recommended_next,
    });
    void resumable.refetch();
  }

  // Completion handoff — never a dead end (Focus Flow doctrine).
  if (completion) {
    return (
      <main
        className="min-h-screen bg-[color:var(--brand-obsidian,#0B0D12)] px-5 py-10"
        data-testid="focus-flow-demo-complete"
      >
        <MissionCompleteHandoff
          headline="Everything's ready."
          detail="You've completed the Focus Flow demo mission."
          handoff={{
            recommended_next: completion.recommended_next,
            allowed_next_missions: completion.recommended_next
              ? [completion.recommended_next]
              : [],
          }}
          onAcceptHandoff={() => navigate("/home")}
          acceptLabel="Return home"
        />
      </main>
    );
  }

  // Active mission — render the shell.
  if (activeMission) {
    const canComplete = activeMission.stage === "confirmation";
    return (
      <MissionShell
        mission={activeMission}
        missionTitle="Focus Flow demo mission"
        stageLabel={STAGE_LABEL[activeMission.stage]}
        primaryActionLabel={canComplete ? "Complete mission" : "Continue"}
        primaryActionState={
          advanceMutation.isPending || completeMutation.isPending
            ? "pending" : "idle"
        }
        onPrimaryAction={canComplete ? handleComplete : handleAdvance}
        onPause={handlePause}
        status={`Version ${activeMission.version}`}
      >
        <div className="space-y-4">
          <p
            className="text-sm text-white/80 leading-relaxed"
            data-testid="focus-flow-demo-decision-copy"
          >
            You're on step {activeMission.progress.current + 1} of{" "}
            {activeMission.progress.total || STAGE_ORDER.length}. Confirm you're
            ready to move to the next step — Akinex will handle the rest.
          </p>
          <div
            className="rounded-xl border border-white/10 bg-white/5 p-4 text-xs text-white/70"
            data-testid="focus-flow-demo-memory"
          >
            <div className="font-medium text-white/90 mb-1">Mission memory</div>
            <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">
              {JSON.stringify(activeMission.memory.data, null, 2)}
            </pre>
          </div>
        </div>
      </MissionShell>
    );
  }

  // Landing — start or resume.
  return (
    <main
      className="min-h-screen bg-[color:var(--brand-obsidian,#0B0D12)] px-5 py-10 md:py-14"
      data-testid="focus-flow-demo-landing"
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <header>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80">
            Focus Flow · demo
          </div>
          <h1 className="mt-1 text-2xl md:text-3xl font-semibold text-white">
            Ready when you are.
          </h1>
          <p className="mt-2 text-sm text-white/70 max-w-lg">
            This is the Focus Flow framework driving a demo mission. Start a
            new mission, pause it any time, and Orkexra will remember exactly
            where you left off.
          </p>
        </header>

        {demoResumables.length > 0 && (
          <section
            className="space-y-3"
            data-testid="focus-flow-demo-resumable-section"
          >
            <h2 className="text-sm font-medium text-white/80">Resume your work</h2>
            {demoResumables.map((m) => (
              <MissionResumeTile
                key={m.id}
                mission={m}
                displayName="Focus Flow demo"
                stageDetail={`${STAGE_LABEL[m.stage]} · Step ${m.progress.current + 1}`}
                onResume={() => handleResume(m)}
              />
            ))}
          </section>
        )}

        <button
          type="button"
          onClick={handleStart}
          disabled={startMutation.isPending}
          className="rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60 transition"
          data-testid="focus-flow-demo-start"
        >
          {startMutation.isPending ? "Starting…" : "Start demo mission"}
        </button>
      </div>
    </main>
  );
}
